import React from "react";
import { Space } from "antd";
import { AxTreeSelect, AxDivider, AxButton } from "@dynata/axiom3";

class TreeSelect extends React.Component {
  render() {
    return (
      <div className="section" id="treeselect">
        <h3>Tree Select</h3>
        <p>Tree selection control.</p>
        <AxDivider orientation="left" plain>
          Basic Usage
        </AxDivider>
        <Space direction="vertical">
          <Space size="large" direction="vertical">
            <pre>{` <AxTree treeData={treeData} />`}</pre>
            <div className="prop-header">
              <p className="props-title">Props</p>
              <p>
                For AntD props, visit{" "}
                <AxButton
                  size="small"
                  type="link"
                  target="_blank"
                  href="https://ant.design/components/tree-select/#API"
                >
                  https://ant.design/components/tree-select/#API
                </AxButton>
              </p>
            </div>

            <table className="props-table">
              <thead>
                <tr>
                  <th>Property</th>
                  <th>Description</th>
                  <th>Type</th>
                  <th>Default</th>
                  <th>Version</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>allowClear</td>
                  <td>Whether allow clear</td>
                  <td>boolean</td>
                  <td>false</td>
                  <td></td>
                </tr>
                <tr>
                  <td>autoClearSearchValue</td>
                  <td>
                    If auto clear search input value when multiple select is
                    selected/deselected
                  </td>
                  <td>boolean</td>
                  <td>true</td>
                  <td></td>
                </tr>
                <tr>
                  <td>bordered</td>
                  <td>Whether has border style</td>
                  <td>boolean</td>
                  <td>true</td>
                  <td></td>
                </tr>
                <tr>
                  <td>defaultValue</td>
                  <td>To set the initial selected treeNode(s)</td>
                  <td>string | string[]</td>
                  <td>-</td>
                  <td></td>
                </tr>
                <tr>
                  <td>disabled</td>
                  <td>Disabled or not</td>
                  <td>boolean</td>
                  <td>false</td>
                  <td></td>
                </tr>
                <tr>
                  <td>popupClassName</td>
                  <td>The className of dropdown menu</td>
                  <td>string</td>
                  <td>-</td>
                  <td>4.23.0</td>
                </tr>
                <tr>
                  <td>dropdownMatchSelectWidth</td>
                  <td>
                    Determine whether the dropdown menu and the select input are
                    the same width. Default set <code>min-width</code> same as
                    input. Will ignore when value less than select width.{" "}
                    <code>false</code> will disable virtual scroll
                  </td>
                  <td>boolean | number</td>
                  <td>true</td>
                  <td></td>
                </tr>
                <tr>
                  <td>dropdownRender</td>
                  <td>Customize dropdown content</td>
                  <td>(originNode: ReactNode, props) =&gt; ReactNode</td>
                  <td>-</td>
                  <td></td>
                </tr>
                <tr>
                  <td>dropdownStyle</td>
                  <td>To set the style of the dropdown menu</td>
                  <td>CSSProperties</td>
                  <td>-</td>
                  <td></td>
                </tr>
                <tr>
                  <td>fieldNames</td>
                  <td>Customize node label, value, children field name</td>
                  <td>object</td>
                  <td>
                    <pre>{`{ label: <code>label</code>, value: <code>value</code>, children: <code>children</code> }`}</pre>
                  </td>
                  <td>4.17.0</td>
                </tr>
                <tr>
                  <td>filterTreeNode</td>
                  <td>
                    Whether to filter treeNodes by input value. The value of{" "}
                    <code>treeNodeFilterProp</code> is used for filtering by
                    default
                  </td>
                  <td>
                    boolean | function(inputValue: string, treeNode: TreeNode)
                    (should return boolean)
                  </td>
                  <td>function</td>
                  <td></td>
                </tr>
                <tr>
                  <td>getPopupContainer</td>
                  <td>
                    To set the container of the dropdown menu. The default is to
                    create a <code>div</code> element in <code>body</code>, you
                    can reset it to the scrolling area and make a relative
                    reposition.{" "}
                    <a href="https://codepen.io/afc163/pen/zEjNOy?editors=0010">
                      example
                    </a>
                  </td>
                  <td>function(triggerNode)</td>
                  <td>() =&gt; document.body</td>
                  <td></td>
                </tr>
                <tr>
                  <td>labelInValue</td>
                  <td>
                    Whether to embed label in value, turn the format of value
                    from <code>string</code> to{" "}
                    <pre>{`{value: string, label: ReactNode, halfChecked: string[]}`}</pre>
                  </td>
                  <td>boolean</td>
                  <td>false</td>
                  <td></td>
                </tr>
                <tr>
                  <td>listHeight</td>
                  <td>Config popup height</td>
                  <td>number</td>
                  <td>256</td>
                  <td></td>
                </tr>
                <tr>
                  <td>loadData</td>
                  <td>Load data asynchronously</td>
                  <td>function(node)</td>
                  <td>-</td>
                  <td></td>
                </tr>
                <tr>
                  <td>maxTagCount</td>
                  <td>
                    Max tag count to show. <code>responsive</code> will cost
                    render performance
                  </td>
                  <td>
                    number | <code>responsive</code>
                  </td>
                  <td>-</td>
                  <td>responsive: 4.10</td>
                </tr>
                <tr>
                  <td>maxTagPlaceholder</td>
                  <td>Placeholder for not showing tags</td>
                  <td>ReactNode | function(omittedValues)</td>
                  <td>-</td>
                  <td></td>
                </tr>
                <tr>
                  <td>multiple</td>
                  <td>
                    Support multiple or not, will be <code>true</code> when
                    enable <code>treeCheckable</code>
                  </td>
                  <td>boolean</td>
                  <td>false</td>
                  <td></td>
                </tr>
                <tr>
                  <td>notFoundContent</td>
                  <td>Specify content to show when no result matches</td>
                  <td>ReactNode</td>
                  <td>
                    <code>Not Found</code>
                  </td>
                  <td></td>
                </tr>
                <tr>
                  <td>placeholder</td>
                  <td>Placeholder of the select input</td>
                  <td>string</td>
                  <td>-</td>
                  <td></td>
                </tr>
                <tr>
                  <td>placement</td>
                  <td>The position where the selection box pops up</td>
                  <td>
                    <code>bottomLeft</code> <code>bottomRight</code>{" "}
                    <code>topLeft</code> <code>topRight</code>
                  </td>
                  <td>bottomLeft</td>
                  <td></td>
                </tr>
                <tr>
                  <td>searchValue</td>
                  <td>
                    Work with <code>onSearch</code> to make search value
                    controlled
                  </td>
                  <td>string</td>
                  <td>-</td>
                  <td></td>
                </tr>
                <tr>
                  <td>showArrow</td>
                  <td>
                    Whether to show the <code>suffixIcon</code>，when single
                    selection mode, default <code>true</code>
                  </td>
                  <td>boolean</td>
                  <td>-</td>
                  <td></td>
                </tr>
                <tr>
                  <td>showCheckedStrategy</td>
                  <td>
                    The way show selected item in box when{" "}
                    <code>treeCheckable</code> set. <strong>Default:</strong>{" "}
                    just show child nodes.{" "}
                    <strong>
                      <code>TreeSelect.SHOW_ALL</code>:
                    </strong>{" "}
                    show all checked treeNodes (include parent treeNode).{" "}
                    <strong>
                      <code>TreeSelect.SHOW_PARENT</code>:
                    </strong>{" "}
                    show checked treeNodes (just show parent treeNode)
                  </td>
                  <td>
                    <code>TreeSelect.SHOW_ALL</code> |{" "}
                    <code>TreeSelect.SHOW_PARENT</code> |{" "}
                    <code>TreeSelect.SHOW_CHILD</code>
                  </td>
                  <td>
                    <code>TreeSelect.SHOW_CHILD</code>
                  </td>
                  <td></td>
                </tr>
                <tr>
                  <td>showSearch</td>
                  <td>Support search or not</td>
                  <td>boolean</td>
                  <td>single: false | multiple: true</td>
                  <td></td>
                </tr>
                <tr>
                  <td>size</td>
                  <td>To set the size of the select input</td>
                  <td>
                    <code>large</code> | <code>middle</code> |{" "}
                    <code>small</code>
                  </td>
                  <td>-</td>
                  <td></td>
                </tr>
                <tr>
                  <td>status</td>
                  <td>Set validation status</td>
                  <td>'error' | 'warning'</td>
                  <td>-</td>
                  <td>4.19.0</td>
                </tr>
                <tr>
                  <td>suffixIcon</td>
                  <td>
                    The custom suffix icon,you must set <code>showArrow</code>{" "}
                    to <code>true</code> manually in multiple selection mode
                  </td>
                  <td>ReactNode</td>
                  <td>-</td>
                  <td></td>
                </tr>
                <tr>
                  <td>switcherIcon</td>
                  <td>Customize collapse/expand icon of tree node</td>
                  <td>
                    ReactNode | ((props: AntTreeNodeProps) =&gt; ReactNode)
                  </td>
                  <td>-</td>
                  <td>renderProps: 4.20.0</td>
                </tr>
                <tr>
                  <td>tagRender</td>
                  <td>
                    Customize tag render when <code>multiple</code>
                  </td>
                  <td>(props) =&gt; ReactNode</td>
                  <td>-</td>
                  <td></td>
                </tr>
                <tr>
                  <td>treeCheckable</td>
                  <td>Whether to show checkbox on the treeNodes</td>
                  <td>boolean</td>
                  <td>false</td>
                  <td></td>
                </tr>
                <tr>
                  <td>treeCheckStrictly</td>
                  <td>
                    Whether to check nodes precisely (in the{" "}
                    <code>checkable</code> mode), means parent and child nodes
                    are not associated, and it will make{" "}
                    <code>labelInValue</code> be true
                  </td>
                  <td>boolean</td>
                  <td>false</td>
                  <td></td>
                </tr>
                <tr>
                  <td>treeData</td>
                  <td>
                    Data of the treeNodes, manual construction work is no longer
                    needed if this property has been set(ensure the Uniqueness
                    of each value)
                  </td>
                  <td>
                    array&lt;
                    <pre>{`
                    {
                      (value,
                      title,
                      children,
                      [disabled, disableCheckbox, selectable, checkable])
                    }
                    `}</pre>
                    &gt;
                  </td>
                  <td>[]</td>
                  <td></td>
                </tr>
                <tr>
                  <td>treeDataSimpleMode</td>
                  <td>
                    Enable simple mode of treeData. Changes the{" "}
                    <code>treeData</code> schema to:{" "}
                    <pre>{`[{id:1, pId:0, value:'1', title:"test1",...},...] where pId is parent node's id). It is possible to replace the default id and pId keys by providing object totreeDataSimpleMode`}</pre>
                  </td>
                  <td>
                    boolean | object&lt;
                    <pre>{`{ id: string, pId: string, rootPId: string }&gt;`}</pre>
                  </td>
                  <td>false</td>
                  <td></td>
                </tr>
                <tr>
                  <td>treeDefaultExpandAll</td>
                  <td>Whether to expand all treeNodes by default</td>
                  <td>boolean</td>
                  <td>false</td>
                  <td></td>
                </tr>
                <tr>
                  <td>treeDefaultExpandedKeys</td>
                  <td>Default expanded treeNodes</td>
                  <td>string[]</td>
                  <td>-</td>
                  <td></td>
                </tr>
                <tr>
                  <td>treeExpandAction</td>
                  <td>
                    Tree title open logic when click, optional: false |{" "}
                    <code>click</code> | <code>doubleClick</code>
                  </td>
                  <td>string | boolean</td>
                  <td>false</td>
                  <td>4.21.0</td>
                </tr>
                <tr>
                  <td>treeExpandedKeys</td>
                  <td>Set expanded keys</td>
                  <td>string[]</td>
                  <td>-</td>
                  <td></td>
                </tr>
                <tr>
                  <td>treeIcon</td>
                  <td>
                    Shows the icon before a TreeNode's title. There is no
                    default style; you must set a custom style for it if set to{" "}
                    <code>true</code>
                  </td>
                  <td>boolean</td>
                  <td>false</td>
                  <td></td>
                </tr>
                <tr>
                  <td>treeLoadedKeys</td>
                  <td>
                    (Controlled) Set loaded tree nodes, work with{" "}
                    <code>loadData</code> only
                  </td>
                  <td>string[]</td>
                  <td>[]</td>
                  <td></td>
                </tr>
                <tr>
                  <td>treeLine</td>
                  <td>
                    Show the line. Ref{" "}
                    <a href="/components/tree/#components-tree-demo-line">
                      Tree - showLine
                    </a>
                  </td>
                  <td>boolean | object</td>
                  <td>false</td>
                  <td>4.17.0</td>
                </tr>
                <tr>
                  <td>treeNodeFilterProp</td>
                  <td>
                    Will be used for filtering if <code>filterTreeNode</code>{" "}
                    returns true
                  </td>
                  <td>string</td>
                  <td>
                    <code>value</code>
                  </td>
                  <td></td>
                </tr>
                <tr>
                  <td>treeNodeLabelProp</td>
                  <td>Will render as content of select</td>
                  <td>string</td>
                  <td>
                    <code>title</code>
                  </td>
                  <td></td>
                </tr>
                <tr>
                  <td>value</td>
                  <td>To set the current selected treeNode(s)</td>
                  <td>string | string[]</td>
                  <td>-</td>
                  <td></td>
                </tr>
                <tr>
                  <td>virtual</td>
                  <td>Disable virtual scroll when set to false</td>
                  <td>boolean</td>
                  <td>true</td>
                  <td>4.1.0</td>
                </tr>
                <tr>
                  <td>onChange</td>
                  <td>
                    A callback function, can be executed when selected treeNodes
                    or input value change
                  </td>
                  <td>function(value, label, extra)</td>
                  <td>-</td>
                  <td></td>
                </tr>
                <tr>
                  <td>onDropdownVisibleChange</td>
                  <td>Called when dropdown open</td>
                  <td>function(open)</td>
                  <td>-</td>
                  <td></td>
                </tr>
                <tr>
                  <td>onSearch</td>
                  <td>
                    A callback function, can be executed when the search input
                    changes
                  </td>
                  <td>function(value: string)</td>
                  <td>-</td>
                  <td></td>
                </tr>
                <tr>
                  <td>onSelect</td>
                  <td>
                    A callback function, can be executed when you select a
                    treeNode
                  </td>
                  <td>function(value, node, extra)</td>
                  <td>-</td>
                  <td></td>
                </tr>
                <tr>
                  <td>onTreeExpand</td>
                  <td>
                    A callback function, can be executed when treeNode expanded
                  </td>
                  <td>function(expandedKeys)</td>
                  <td>-</td>
                  <td></td>
                </tr>
                <tr>
                  <td>...others</td>
                  <td>
                    All extra props are passed down to the{" "}
                    <>
                      <AxButton
                        size="small"
                        type="link"
                        target="_blank"
                        href="https://4x.ant.design/components/tree-select/#API"
                      >
                        {`<TreeSelect />`}
                      </AxButton>
                    </>{" "}
                    component.
                  </td>
                  <td></td>
                  <td>-</td>
                  <td></td>
                </tr>
              </tbody>
            </table>

            <div className="prop-header">
              <p className="props-title">Methods</p>
            </div>
            <table className="props-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Description</th>
                  <th>Version</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>blur()</td>
                  <td>Remove focus</td>
                  <td></td>
                </tr>
                <tr>
                  <td>focus()</td>
                  <td>Get focus</td>
                  <td></td>
                </tr>
              </tbody>
            </table>

            <div className="prop-header">
              <p className="props-title">Props: TreeNode</p>
            </div>
            <table className="props-table">
              <thead>
                <tr>
                  <th>Property</th>
                  <th>Description</th>
                  <th>Type</th>
                  <th>Default</th>
                  <th>Version</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>checkable</td>
                  <td>
                    When Tree is checkable, set TreeNode display Checkbox or not
                  </td>
                  <td>boolean</td>
                  <td>-</td>
                  <td></td>
                </tr>
                <tr>
                  <td>disableCheckbox</td>
                  <td>Disables the checkbox of the treeNode</td>
                  <td>boolean</td>
                  <td>false</td>
                  <td></td>
                </tr>
                <tr>
                  <td>disabled</td>
                  <td>Disabled or not</td>
                  <td>boolean</td>
                  <td>false</td>
                  <td></td>
                </tr>
                <tr>
                  <td>isLeaf</td>
                  <td>Leaf node or not</td>
                  <td>boolean</td>
                  <td>false</td>
                  <td></td>
                </tr>
                <tr>
                  <td>key</td>
                  <td>
                    Required property (unless using{" "}
                    <code>treeDataSimpleMode</code>), should be unique in the
                    tree
                  </td>
                  <td>string</td>
                  <td>-</td>
                  <td></td>
                </tr>
                <tr>
                  <td>selectable</td>
                  <td>Whether can be selected</td>
                  <td>boolean</td>
                  <td>true</td>
                  <td></td>
                </tr>
                <tr>
                  <td>title</td>
                  <td>Content showed on the treeNodes</td>
                  <td>ReactNode</td>
                  <td>
                    <code>---</code>
                  </td>
                  <td></td>
                </tr>
                <tr>
                  <td>value</td>
                  <td>
                    Will be treated as <code>treeNodeFilterProp</code> by
                    default, should be unique in the tree
                  </td>
                  <td>string</td>
                  <td>-</td>
                  <td></td>
                </tr>
              </tbody>
            </table>

            <pre>{`<AxTreeSelect
  style={{ width: "100%" }}
  dropdownStyle={{ maxHeight: 400, overflow: "auto" }}
  placeholder="Please select"
  allowClear
  treeDefaultExpandAll
>
  <AxTreeSelect.Treenode value="parent 1" title="parent 1">
    <AxTreeSelect.Treenode value="parent 1-0" title="parent 1-0">
      <AxTreeSelect.Treenode value="leaf1" title="leaf1" />
      <AxTreeSelect.Treenode value="leaf2" title="leaf2" />
    </AxTreeSelect.Treenode>
    <AxTreeSelect.Treenode value="parent 1-1" title="parent 1-1">
      <AxTreeSelect.Treenode value="leaf3" title={<b>leaf3</b>} />
    </AxTreeSelect.Treenode>
  </AxTreeSelect.Treenode>
</AxTreeSelect>`}</pre>
          </Space>
        </Space>

        <AxDivider orientation="left" plain>
          Default
        </AxDivider>

        <AxTreeSelect
          style={{ width: "100%" }}
          dropdownStyle={{ maxHeight: 400, overflow: "auto" }}
          placeholder="Please select"
          allowClear
          treeDefaultExpandAll
        >
          <AxTreeSelect.Treenode value="parent 1" title="parent 1">
            <AxTreeSelect.Treenode value="parent 1-0" title="parent 1-0">
              <AxTreeSelect.Treenode value="leaf1" title="leaf1" />
              <AxTreeSelect.Treenode value="leaf2" title="leaf2" />
            </AxTreeSelect.Treenode>
            <AxTreeSelect.Treenode value="parent 1-1" title="parent 1-1">
              <AxTreeSelect.Treenode value="leaf3" title={<b>leaf3</b>} />
            </AxTreeSelect.Treenode>
          </AxTreeSelect.Treenode>
        </AxTreeSelect>
      </div>
    );
  }
}
export default TreeSelect;
